import {
    ScoopButton,
    ScoopDialog,
    ScoopSelector,
    ScoopSpinner,
    ScoopText,
    ScoopSearchBar,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../ui'
import { useEffect, useMemo, useState } from 'react'
import { Server } from '../../../../../api/Server'
import { useDispatch, useSelector } from 'react-redux'
import { APIConnector } from '../../../Appconnect/API/APIConnector'
import { useNavigate } from 'react-router-dom'
import { debounce, toLower } from 'lodash'
import { ScoopTheme } from '../../../../Insights/Style'
import { EVENTS, trackEvent } from '../../../../../events'
import { setShowRecipeModal } from '../../../../../store/actions/uiActions'
import { useCanvases } from '../../../../../api/hooks/canvases/useCanvases'
import styles from './NewFromRecipe.module.scss'

export const NewFromRecipe = ({ isOpen, setOpen, createdInbox }) => {
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)
    const openByOnboarding = useSelector((state) => state.ui.showRecipeModal)

    const [server, setServer] = useState(new Server(workspaceID, userID, token))
    const [recipeMaps, setRecipeMaps] = useState(null)
    // This is initialized with the same value as recipeMaps and never mutated
    const [recipeMapsCopy, setRecipeMapsCopy] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [selectedConnector, setSelectedConnector] = useState(null)
    const [step, setStep] = useState(0)
    const [selectedMap, setSelectedMap] = useState(null)
    const [connectionKey, setConnectionKey] = useState(null)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const debounceFn = debounce((value) => {
        // if length of value is over 5 chars, log event
        if (value.length > 5) {
            trackEvent(EVENTS.CANVAS.SEARCH_RECIPE, {
                recipe: {
                    name: value,
                },
            })
        }
        recipeMaps &&
            setRecipeMaps(
                recipeMapsCopy.filter((recipeMap) =>
                    recipeMap.recipeMapName.toLowerCase().includes(toLower(value))
                )
            )
    }, 200)
    const { refetch } = useCanvases()

    const categories = useMemo(() => {
        let categories = []
        categories.push('All')
        recipeMapsCopy &&
            recipeMapsCopy.forEach((recipeMap) => {
                if (Array.isArray(recipeMap.recipeCategory)) {
                    recipeMap.recipeCategory.forEach((category) => {
                        if (!categories.includes(category)) {
                            categories.push(category)
                        }
                    })
                } else {
                    if (!categories.includes(recipeMap.recipeCategory)) {
                        categories.push(recipeMap.recipeCategory)
                    }
                }
            })
        setSelectedCategory(categories[0])
        return categories
    }, [recipeMapsCopy])

    const categoryOptions = useMemo(() => {
        return categories.map((category) => ({
            label: category,
            value: category,
        }))
    }, [categories])

    const connectors = useMemo(() => {
        let connectors = []
        connectors.push('All')
        recipeMapsCopy &&
            recipeMapsCopy.forEach((recipeMap) => {
                recipeMap.connectors.forEach((connector) => {
                    if (!connectors.includes(connector)) {
                        connectors.push(connector)
                    }
                })
            })
        setSelectedConnector(connectors[0])
        return connectors
    }, [recipeMapsCopy])

    const connectorOptions = useMemo(() => {
        return connectors.map((connector) => ({
            label: connector,
            value: connector,
        }))
    }, [connectors])

    useEffect(() => {
        if (userID && token && workspaceID) setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    useEffect(() => {
        const recipeMapping = localStorage.getItem('recipeMapping')
        if (recipeMapping && recipeMapping !== 'ALL' && recipeMapsCopy) {
            const recipe = recipeMapsCopy.find((recipe) => recipe.mappingID === recipeMapping)
            setSelectedMap(recipe)
            recipe ? setStep(1) : setStep(0)
        }
        if (createdInbox && recipeMapsCopy)
            handleConnectorChange({
                target: { value: createdInbox?.extractDefinition?.connectorType || 'All' },
            })
    }, [recipeMapsCopy])

    useEffect(() => {
        const recipeMapping = localStorage.getItem('recipeMapping')
        if (recipeMapping && recipeMapping !== 'ALL') setStep(1)
        if (server?.token) {
            setIsLoading(true)
            server.postData(
                {
                    action: 'getRecipeMappings',
                },
                (results) => {
                    if (results.recipes) {
                        setRecipeMaps(results.recipes)
                        setRecipeMapsCopy(results.recipes)
                        setIsLoading(false)
                    }
                },
                undefined,
                () => setIsLoading(false)
            )
        }
    }, [])

    const handleSearchChange = (event) => {
        debounceFn(event.target.value)
    }

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value)
        if (event.target.value === 'All') {
            setRecipeMaps([...recipeMapsCopy])
        } else {
            const newRecipeMaps = recipeMapsCopy.filter((recipeMap) => {
                if (Array.isArray(recipeMap.recipeCategory)) {
                    return recipeMap.recipeCategory.includes(event.target.value)
                } else {
                    return recipeMap.recipeCategory === event.target.value
                }
            })
            setRecipeMaps(newRecipeMaps)
        }
    }

    const handleConnectorChange = (event) => {
        setSelectedConnector(event.target.value)
        if (event.target.value === 'All') {
            setRecipeMaps([...recipeMapsCopy])
        } else {
            const newRecipeMaps = recipeMapsCopy.filter((recipeMap) => {
                return recipeMap.connectors.includes(event.target.value)
            })
            setRecipeMaps(newRecipeMaps)
        }
    }

    const getColorForCategory = (category) => {
        const index = categories.indexOf(category)
        return ScoopTheme.color[index - 1]
    }

    const renderCategoryPill = (category) => {
        if (Array.isArray(category)) {
            return category.map((cat, index) => {
                const cssVars = {
                    '--pill-color': getColorForCategory(cat),
                }
                return (
                    <div key={index} className={styles.categoryPill} style={cssVars}>
                        {cat}
                    </div>
                )
            })
        } else {
            const cssVars = {
                '--pill-color': getColorForCategory(category),
            }
            return (
                <div className={styles.categoryPill} style={cssVars}>
                    {category}
                </div>
            )
        }
    }

    const RecipeCard = ({ recipeMap, clickable = true }) => {
        return (
            <div className={styles.recipeCardWrapper}>
                <ScoopColumn
                    className={`${styles.recipeCard} ${recipeMap.recipeMapName === selectedMap?.recipeMapName ? styles.selected : ''} ${!clickable ? styles.removeHover : ''}`}
                    onClick={() => {
                        if (!clickable) return
                        setSelectedMap(recipeMap)
                        localStorage.removeItem('recipeMapping')
                    }}
                >
                    <ScoopRow className={styles.recipeCardHeader}>
                        <img src={recipeMap.logoURL} className={styles.recipeLogo} />
                        <ScoopText className={styles.recipeName}>
                            {recipeMap.recipeMapName}
                        </ScoopText>
                    </ScoopRow>
                    <ScoopRow className={styles.recipeCardContent}>
                        <ScoopColumn className={styles.recipeCardLeft}>
                            <ScoopText className={styles.recipeDescription}>
                                {recipeMap.descriptionHTML}
                            </ScoopText>
                            <ScoopRow className={styles.categoryPillsContainer}>
                                {renderCategoryPill(recipeMap.recipeCategory)}
                            </ScoopRow>
                        </ScoopColumn>
                        <div>
                            <img
                                src={recipeMap.screenshotURL}
                                className={styles.recipeScreenshot}
                                alt={'image'}
                            />
                        </div>
                    </ScoopRow>
                </ScoopColumn>
            </div>
        )
    }

    const renderSelectMapping = () => {
        return (
            <ScoopDialog
                open={isOpen}
                maxWidth="xl"
                onClose={() => handleClose()}
                actions={
                    <>
                        <ScoopButton variant="outlined" onClick={onClose}>
                            Cancel
                        </ScoopButton>
                        {selectedMap && selectedMap.mappingID && step === 0 && (
                            <ScoopButton
                                variant="contained"
                                onClick={() => {
                                    trackEvent(EVENTS.CANVAS.CREATE_FROM_RECIPE_ATTEMPT, {
                                        recipe: {
                                            name: selectedMap.recipeMapName,
                                        },
                                    })
                                    setStep(1)
                                }}
                            >
                                Next
                            </ScoopButton>
                        )}
                    </>
                }
                containerStyle={{ height: '100%' }}
                contentStyle={{ height: 'calc(100% - 34px)' }}
            >
                <ScoopColumn className={styles.headerSection}>
                    <ScoopText className={styles.title}>Create New Canvas from Recipe</ScoopText>
                    <ScoopText className={styles.descriptionText}>
                        Select from a list of pre-defined recipes
                    </ScoopText>
                </ScoopColumn>
                <ScoopRow className={styles.filterSelectorsContainer}>
                    {categoryOptions && categoryOptions.length > 0 && (
                        <div>
                            <ScoopSelector
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                options={categoryOptions}
                                label="Filter by category"
                                labelClassName="selector-label-bold"
                                className={styles.connectorSelect}
                            />
                        </div>
                    )}
                    {connectorOptions && connectorOptions.length > 0 && (
                        <div>
                            <ScoopSelector
                                value={selectedConnector}
                                onChange={handleConnectorChange}
                                options={connectorOptions}
                                label="Filter by application"
                                labelClassName="selector-label-bold"
                                className={styles.connectorSelect}
                            />
                        </div>
                    )}
                    <ScoopSearchBar
                        placeholder={'Search Recipes'}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </ScoopRow>
                {isLoading ? (
                    <ScoopCenter className={styles.loadingState}>
                        <ScoopSpinner size={72} />
                    </ScoopCenter>
                ) : (
                    <div className={styles.recipesGrid}>
                        {recipeMaps && recipeMaps.length > 0 ? (
                            recipeMaps.map((recipeMap, index) => {
                                return (
                                    <RecipeCard key={index} recipeMap={recipeMap} index={index} />
                                )
                            })
                        ) : (
                            <ScoopCenter className={styles.emptyState}>
                                <ScoopText className={styles.emptyStateText}>
                                    No recipes found
                                </ScoopText>
                            </ScoopCenter>
                        )}
                    </div>
                )}
            </ScoopDialog>
        )
    }

    const onClose = () => {
        openByOnboarding ? dispatch(setShowRecipeModal(false)) : setOpen(false)
        if (localStorage.getItem('recipeMapping')) {
            localStorage.removeItem('recipeMapping')
        }
    }

    const handleClose = () => {
        openByOnboarding ? dispatch(setShowRecipeModal(false)) : setOpen(false)
        setStep(0)
        localStorage.removeItem('recipeMapping')
    }

    const getCanvasList = (server, next) => {
        server.postData(
            {
                action: 'getObjects',
                class: 'scoop.canvas.Canvas',
                attributeName: 'workspaceID',
                attributeValue: workspaceID,
                noCache: true,
            },
            (results) => {
                if (next) next(results.objects)
            }
        )
    }

    const testDone = (server, interval, previousCanvases) => {
        if (server?.token) {
            server.postData(
                {
                    action: 'getProcessStatus',
                    workspaceID: workspaceID,
                },
                (results) => {
                    if (results.inboxStatus) {
                        if (
                            !results.inboxStatus.some(
                                (s) => s.type === 'instantiateRecipe' || s.type === 'extract'
                            )
                        ) {
                            getCanvasList(server, (newCanvasList) => {
                                if (newCanvasList.length > previousCanvases.length) {
                                    setStep(0)
                                    const newCanvas = newCanvasList.reduce((maxCanvas, canvas) => {
                                        return Number(canvas.canvasID.slice(1)) >
                                            Number(maxCanvas?.canvasID.slice(1) || -Infinity)
                                            ? canvas
                                            : maxCanvas
                                    }, null)
                                    clearInterval(interval)
                                    {
                                        if (localStorage.getItem('recipeMapping')) {
                                            trackEvent(EVENTS.CANVAS.RECIPE_PRODUCT_INVOCATION, {
                                                recipe: {
                                                    name: selectedMap.recipeMapName,
                                                },
                                            })
                                        } else {
                                            trackEvent(EVENTS.CANVAS.CREATE_FROM_RECIPE_COMPLETE, {
                                                canvas: {
                                                    canvasID: newCanvas.canvasID,
                                                },
                                                recipe: {
                                                    name: selectedMap.recipeMapName,
                                                },
                                            })

                                            // userflow.track('new_canvas', {
                                            //     type: 'recipe',
                                            //     recipe: selectedMap.recipeMapName,
                                            //     name: newCanvas.canvasName,
                                            // })
                                        }
                                    }
                                    onClose()
                                    if (newCanvasList.length > 1) {
                                        newCanvasList.forEach((newCanvas) => {
                                            if (
                                                !previousCanvases.some(
                                                    (canvas) =>
                                                        canvas.canvasID === newCanvas.canvasID
                                                )
                                            ) {
                                                dispatch({
                                                    type: 'UPDATE_CANVAS',
                                                    canvasID: '',
                                                    canvasName: '',
                                                })
                                                dispatch({
                                                    type: 'APPLY_LOADED_OBJECTS_STATE',
                                                    payload: [],
                                                })
                                                navigate(`${newCanvas.canvasID}`, {
                                                    state: 'newCanvas',
                                                })
                                            }
                                        })
                                    } else {
                                        dispatch({
                                            type: 'UPDATE_CANVAS',
                                            canvasID: '',
                                            canvasName: '',
                                        })
                                        dispatch({
                                            type: 'APPLY_LOADED_OBJECTS_STATE',
                                            payload: [],
                                        })
                                        navigate(`${newCanvasList[0].canvasID}`, {
                                            state: 'newCanvas',
                                        })
                                    }
                                    refetch()
                                }
                            })
                        }
                    }
                }
            )
        }
    }

    const handleConfirm = () => {
        if (server?.token) {
            server.sheetPostData(
                {
                    action: 'instantiateRecipe',
                    recipeMap: selectedMap,
                    connectionKey: connectionKey,
                },
                (results) => {
                    if (results.status && results.status === 'instantiatingRecipe') {
                        setStep(3)
                        getCanvasList(server, (list) => {
                            const interval = setInterval(() => {
                                if (isOpen) {
                                    testDone(server, interval, list)
                                } else {
                                    clearInterval(interval)
                                }
                            }, 3000)
                        })
                    }
                }
            )
        }
    }

    switch (step) {
        case 0:
            return renderSelectMapping()
        case 1:
            return (
                <APIConnector
                    open={step === 1}
                    onClose={onClose}
                    stepBack={() => setStep(0)}
                    stepConnected={() => {
                        setStep(2)
                    }}
                    connectorType={selectedMap?.connectors[0]}
                    handleConnectOnly={true}
                    setKey={setConnectionKey}
                    RecipeCard={<RecipeCard recipeMap={selectedMap} clickable={false} />}
                    loading={isLoading || !selectedMap}
                />
            )
        case 2:
            return (
                <ScoopDialog
                    open={step === 2}
                    onClose={handleClose}
                    actions={
                        <>
                            <ScoopButton variant="outlined" onClick={handleClose}>
                                Cancel
                            </ScoopButton>
                            <ScoopButton variant="contained" onClick={handleConfirm}>
                                Confirm
                            </ScoopButton>
                        </>
                    }
                    title="Confirm"
                >
                    <ScoopText>
                        Confirm that you would like to create a new Canvas based on the following
                        recipe and data source
                    </ScoopText>
                    <ScoopColumn className={styles.confirmationGrid}>
                        <ScoopRow>
                            <ScoopText className={styles.confirmationLabel}>Recipe:</ScoopText>
                            <ScoopText className={styles.confirmationValue}>
                                {selectedMap.recipeMapName}
                            </ScoopText>
                        </ScoopRow>
                        <ScoopRow>
                            <ScoopText className={styles.confirmationLabel}>Data Source:</ScoopText>
                            <ScoopText className={styles.confirmationValue}>
                                {selectedMap.connectorType}
                            </ScoopText>
                        </ScoopRow>
                    </ScoopColumn>
                </ScoopDialog>
            )
        case 3:
            return (
                <ScoopDialog open={step === 3} title="Setting Up Recipe" showCloseButton={false}>
                    <ScoopColumn className={styles.loadingContainer}>
                        <ScoopText className={styles.loadingText}>
                            Setting up your Scoop recipe
                        </ScoopText>
                        <ScoopSpinner size={72} className={styles.loadingSpinner} />
                    </ScoopColumn>
                </ScoopDialog>
            )
    }
    return null
}
